import * as XLSX from "xlsx";

type ExportColumn<T> = {
  key: string;
  name: string;
  formatter?: (value: any, row: T) => any;
};


/**
 * Exporta un array de objetos a un archivo Excel usando react-export-table-to-excel.
 *
 * @template T
 * @param {T[]} data - Array de objetos que se desean exportar.
 * @param {ExportColumn} keysToExport - Array de objetos que define qué campos del objeto exportar y su nombre en el encabezado.
 * @param {string} fileName - Nombre del archivo Excel a generar (.xlsx).
 * @param {string} sheetName - Nombre de la hoja dentro del archivo Excel.
 *
 * @example
 * const data = [
 *   { name: "Juan", age: 30 },
 *   { name: "Ana", age: 25 }
 * ];
 * const keys = [
 *   { key: "name", name: "Nombre" },
 *   { key: "age", name: "Edad" }
 * ];
 * exportToExcelData(data, keys, "usuarios.xlsx", "Usuarios");
 */
export function exportToExcelData<T extends Record<string, any>>(
  data: T[],
  keysToExport: ExportColumn<T>[],
  fileName: string,
  sheetName: string
) {
  if (!data.length || !keysToExport.length) return;

  // Encabezados
  const header = keysToExport.map(k => k.name);

  // Filas
  const body = data.map(item =>
    keysToExport.map(column => {
      const value = item[column.key];

      return column.formatter
        ? column.formatter(value, item)
        : (value ?? '');
    })
  );

  // Combinar header + body
  const worksheetData = [header, ...body];

  // Crear hoja
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Crear libro
  const workbook = XLSX.utils.book_new();

  // Agregar hoja al libro
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Descargar archivo
  XLSX.writeFile(workbook, fileName);
}